import { INVESTOR_ROLES, MANAGE_USERS_TABS, RESTRICTED_ROLES, USER_TYPES } from '../store/investor/constants';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { DealFilters } from '../store/investor/reducer';
import { toast } from 'react-toastify';
import { ROCKET_SUMMON_OPEN_FAILED_MESSAGE, TALK_TO_US_OWNERS } from './constants/commonConstants';
import { getRocketUrl, openRocketSummonTab } from './rocketSummon';
import { USER_TYPE } from './constants/user';
import { CountryCodeData } from '../modules/Company/CompanyLogin/CountryCodesDropdown/CountryCodesDropdown';
import { getPresignedUrl } from '../modules/Company/UploadJourney/components/UploadCard/utils/api/helpers';

export const LAKH = 100000;
export const CRORE = 10000000;

export const APP_ENV = {
    PRODUCTION: 'production',
    DEV: 'development',
    STAGING: 'staging',
};

export const history: any = {
    navigate: null,
    location: null,
};

export interface ExtractPhoneNumberResult {
    phoneNumber: string;
    countryCodeIndex: number;
}

export const isMobile = window.innerWidth < 800;
export const emailRegex = new RegExp(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/);
export const websiteUrlRegex = new RegExp(
    /^(?:(ftp|http|https):\/\/)?(?:([a-zA-Z0-9-]+)\.)?(?:[\w-]+\.)+[a-z]{2,}(?:\.[a-z]{2,})?(?:\/)?$/,
);

// The host must END at linkedin.com. The previous `linkedin\.[a-z.]+` let the TLD position
// swallow further labels, so `linkedin.evil.com/in/jane` passed — and this value is published to
// an external scraper, which underwriting-engine now refuses by parsing the host.
export const linkedInRegex = /^(https?:\/\/)?([a-z0-9-]+\.)*linkedin\.com\/(in|company|school|groups)\/[A-Za-z0-9_-]+(\/)?(\?.*)?$/;

export const indianMobileRegex = /^[6-9][0-9]{9}$/;

export let is_prod = process.env.REACT_APP_ENVIRONMENT === 'production' && location.hostname !== 'localhost';

export function isEmpty(value: any) {
    return (
        value === undefined ||
        value === null ||
        // || value === NaN
        (typeof value === 'object' && Object.keys(value).length === 0) ||
        (typeof value === 'string' && value.trim().length === 0) ||
        (typeof value === 'number' && isNaN(Number(value)))
    );
}

/** Formats a number for display: integers without decimals, floats up to 2 dp. */
export const fmt = (n: number): string => {
    if (Number.isInteger(n)) return n.toLocaleString();
    return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

/**
 * Checks if all keys in an object are empty (using isEmpty).
 * Returns true if all keys are empty, false if at least one key has a non-empty value.
 */
export function isAllKeysEmpty(obj: Record<string, any> | null | undefined): boolean {
    if (!obj || typeof obj !== 'object') return true;
    return Object.keys(obj).every((key) => isEmpty(obj[key]));
}

export function getSubdomain() {
    const url = window.location.toString();
    const regex = /^https:\/\/([a-zA-Z0-9.-]+\.[a-zA-Z]{2,})(\/[^\s?]*)?(?:\?[^#\s]*)?$/;
    const match = url.match(regex);
    if (match && match.length > 1) {
        return match?.[1]?.split('.')?.[0];
    }

    if (process.env.REACT_APP_ENVIRONMENT === APP_ENV.STAGING) return 'aica-staging';
    if (process.env.REACT_APP_ENVIRONMENT !== APP_ENV.PRODUCTION) return 'aica-dev';
    return '';
}

export function getDomain() {
    if (process.env.REACT_APP_ENVIRONMENT !== APP_ENV.PRODUCTION) return 'founderlink.ai';
    const hostname = window.location.hostname;
    let parts = hostname.split('.');
    if (parts.length <= 2) return hostname;

    parts = parts.slice(-3);
    if (['co', 'com'].indexOf(parts[1]) > -1) return parts.join('.');

    return parts.slice(-2).join('.');
}

export function convertArrayToMap(arr, key): {}[] {
    return arr.reduce(function (map, obj) {
        map[obj[key]] = obj;
        return map;
    }, {});
}

export function getInitialsFromName(string, takeLastWord = true) {
    if (!string) return '';

    const names = string.trim().split(/\s+/);
    let initials = names[0]?.substring(0, 1).toUpperCase() || '';

    if (names.length > 1) {
        const secondInitial = takeLastWord ? names[names.length - 1]?.substring(0, 1) : names[1]?.substring(0, 1);
        initials += secondInitial?.toUpperCase() || '';
    }

    return initials;
}

export async function copyTextToClipboard(text) {
    if ('clipboard' in navigator) {
        return await navigator.clipboard.writeText(text);
    } else {
        return document.execCommand('copy', true, text);
    }
}

export function getFeatureStatus(availableFeatures: string[] = [], featureName) {
    let value: any = '';
    availableFeatures.map((obj) => {
        const featureKey = Object.keys(obj ?? {})[0];
        if (featureKey === featureName) value = Object?.values(obj)?.[0] || '';
    });
    return value;
}

export const validateGSTUserName = (gstUserName: string) => {
    let isValid = true;
    let message = '';
    if (!gstUserName.match(/^([a-zA-Z0-9_\-.]){8,15}$/)) {
        isValid = false;
        message = 'Please enter a valid username';
    }
    return {
        isValid,
        message,
    };
};

export const clearLocalStorageOnLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
};

export const handleBackgroundScroll = (isScrollable: boolean) => {
    if (isScrollable) {
        document.body.style.overflow = 'auto';
    } else {
        document.body.style.overflow = 'hidden';
    }
};

export const convertToTitleCase = (input = '') => {
    // Check if input contains underscore
    if (input?.includes?.('_')) {
        // Split input by underscore and capitalize each word
        return input
            ?.split?.('_')
            ?.map?.((word) => word?.charAt(0)?.toUpperCase() + word?.slice(1)?.toLowerCase())
            ?.join(' ');
    } else {
        // Split?. camelCase input? into individual words and capitalize them
        return input
            ?.split?.(/(?=[A-Z])/)
            ?.map?.((word) => word?.charAt(0)?.toUpperCase() + word?.slice(1)?.toLowerCase())
            ?.join(' ');
    }
};

export const convertToSnakeCase = (input = '') => {
    // Check if input contains spaces
    if (input?.includes?.(' ')) {
        // Split input by spaces and convert to lowercase
        return input
            ?.split?.(' ')
            ?.map?.((word) => word?.toLowerCase())
            ?.join('_');
    }
};

export const convertToCapitalizeUnderscore = (input: string = ''): string =>
    input
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join('_');

export const getFromLocalStorage = (keyName: string = '') => {
    if (keyName) {
        return localStorage.getItem(keyName) ? JSON.parse(localStorage.getItem(keyName) || '') : null;
    }
    return null;
};

export const getPlatformUserId = (): string => {
    if (sessionStorage.getItem('platform_user_id')) {
        return sessionStorage.getItem('platform_user_id') ?? '';
    }
    return localStorage.getItem('platform_user_id') ?? '';
};

export const isRestrictedUser = (role: null | string, restrictedRoles: string[]) => {
    if (isEmpty(role)) {
        return false;
    }
    if (restrictedRoles.includes(role ?? '')) {
        return true;
    }
    return false;
};
export const numberOnly = (value) => {
    if (!value) return '';
    return value.replace(/[^0-9]/g, '');
};

export function convertToFirstLetterCapital(string) {
    return string.toLowerCase().replace(/\b\w/g, function (letter) {
        return letter.toUpperCase();
    });
}

export function capitalizeWords(str = '') {
    return str?.toLowerCase()?.replace(/\b\w/g, function (char) {
        return char.toUpperCase();
    });
}

export function decodeHtmlEntities(html) {
    return html?.replace(/&#(\d+);/g, function (match, dec) {
        return String.fromCharCode(dec);
    });
}

export function removeKeyAndConvertIdToNumber(array, keyToRemove) {
    array.forEach((obj) => {
        delete obj[keyToRemove];
    });
    return array;
}

export function shortNumberFormat(number) {
    if (Math.abs(number) >= 1.0e6) {
        return (Math.abs(number) / 1.0e6).toFixed(1) + 'M';
    } else if (Math.abs(number) >= 1.0e3) {
        return (Math.abs(number) / 1.0e3).toFixed(0) + 'k';
    } else {
        return number.toString();
    }
}

export function formatWebsiteUrl(websiteUrl = '') {
    let modifiedString = websiteUrl;
    if (websiteUrl?.charAt(websiteUrl?.length - 1) === '/') modifiedString = websiteUrl?.substring(0, websiteUrl?.length - 1);
    return modifiedString?.replace('https://', '')?.replace('http://', '')?.replace('www.', '') || '';
}

export function changeFavicon(newFaviconUrl: string) {
    let link: any = document.querySelector("link[rel~='icon']");
    if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.getElementsByTagName('head')[0].appendChild(link);
    }
    link.href = newFaviconUrl;
}

/**
 * Sets the iOS "Add to Home Screen" icon (apple-touch-icon), creating the link tag if absent.
 * @param newIconUrl - Per-tenant icon URL (same source as changeFavicon) so white-labeled PNOs
 *   get their own home-screen icon instead of iOS auto-generating a plain title-initial monogram.
 */
export function changeAppleTouchIcon(newIconUrl: string) {
    let link: any = document.querySelector("link[rel='apple-touch-icon']");
    if (!link) {
        link = document.createElement('link');
        link.rel = 'apple-touch-icon';
        document.getElementsByTagName('head')[0].appendChild(link);
    }
    link.href = newIconUrl;
}

export function setRolesForUser(role = '') {
    if (RESTRICTED_ROLES.includes(role)) {
        switch (role) {
            case INVESTOR_ROLES.RESTRICTED_UNDERWRITER_ACCESS:
                return 'Underwriter';
            case INVESTOR_ROLES.RESTRICTED_INVESTOR_ACCESS:
                return 'RRF Investor';
        }
    }
    return 'AICA_Investor';
}

export function unflatten(data: { [key: string]: any }, options: { includeNull?: boolean } = { includeNull: false }) {
    const result = {};
    Object.keys(data).forEach((key) => {
        const keys = key.split('.');
        keys.reduce((r: any, e: string, j) => {
            if (!r[e]) {
                if (Number.isNaN(Number(keys[j + 1]))) {
                    if (keys.length - 1 === j) {
                        // Include the value if it's not null/undefined or if includeNull is true
                        if (options.includeNull || (data[key] !== null && data[key] !== undefined)) {
                            r[e] = data[key];
                        }
                    } else {
                        r[e] = {};
                    }
                } else {
                    r[e] = [];
                }
            }
            return r[e];
        }, result);
    });
    return result;
}

export const flatten = (obj: { [key: string]: any }, prefix = '') => {
    let flattened = {};

    for (let key in obj) {
        if (obj?.hasOwnProperty(key)) {
            let newKey = prefix ? `${prefix}.${key}` : key;

            if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
                Object.assign(flattened, flatten(obj[key], newKey));
            } else {
                flattened[newKey] = obj[key];
            }
        }
    }

    return flattened;
};

export const convertEpochToDate = (epoch: number | string) => {
    dayjs.extend(utc);
    dayjs.extend(timezone);

    const utcTime = dayjs.utc(epoch);
    const istTime = utcTime.tz('Asia/Kolkata');

    return istTime.format('hh:mm A, DD MMM YYYY');
};

export function getObjAndFileNameS3(url) {
    if (Number.isInteger(Number(url))) {
        const obj = { url };
        return { obj, fileName: 'file', extention: null };
    }
    const commonStringInUrl = 'amazonaws.com/';
    const indexOfCommonString = url?.indexOf(commonStringInUrl);
    const bucket = getBucketNameFromS3Url(url);
    const urlToSend = url.slice(indexOfCommonString + commonStringInUrl.length);
    const obj = { url: urlToSend, bucket: bucket };
    const fileName = urlToSend.substr(urlToSend.indexOf('/') + 1);

    const extentionArr = obj?.url?.split('.');
    const extention = extentionArr[extentionArr?.length - 1];
    return { obj, fileName, extention };
}

export function getBucketNameFromS3Url(url) {
    const urlParts = url.split('/');
    if (urlParts.length >= 3 && urlParts[2].endsWith('.amazonaws.com')) {
        const urlParts1 = urlParts[2].split('.');
        if (urlParts1.length > 0) {
            return urlParts1[0];
        } else {
            return null;
        }
    } else {
        return null;
    }
}

export function validateCIN(cin: string): boolean {
    const cinPattern = new RegExp(/^([LUu]{1})([0-9]{5})([A-Za-z]{2})([0-9]{4})([A-Za-z]{3})([0-9]{6})$/);
    return cinPattern.test(cin);
}

export const validateDIN = (din) => {
    const dinRegex = /^[0-9]{8}$/;
    if (!din || !dinRegex.test(din)) {
        return false;
    }
    return true;
};

// Length-only check: exactly 10 digits, any leading digit. Kept in sync with the app's other
// phone flows (InviteTeammateModal, DirectorDetails credit-bureau) which all validate on length
// alone — deliberately NOT enforcing the 6-9 Indian-mobile series, so Add Director accepts the
// same numbers the rest of the app does. Digit-only + 10-char cap is enforced at the input.
export const validateTenDigitPhone = (mobileNumber) => {
    const tenDigitRegex = /^\d{10}$/;
    if (!mobileNumber || !tenDigitRegex.test(mobileNumber)) {
        return false;
    }
    return true;
};

export function ensureHttpsPrefix(url?: string | null): string {
    if (!url) return '';
    return /^https?:\/\//i.test(url) ? url : `https://${url}`;
}

/**
 * Pass details in query params to be stored in session storage
 *
 * @param summonDetails
 */

export function openCompanySummonTab(userData: {
    accountType: string;
    parentOrgId: string;
    orgId: string;
    primaryEmail: string;
    userId: string;
    userName: string;
    parentEmailId: string;
    authToken: string;
    userRole: string;
}): void {
    const { orgId, userId, authToken, accountType, parentOrgId, primaryEmail, userName, parentEmailId } = userData;
    // Open a new tab with the summon details in query params
    const summonUrl = `${
        window.location.origin
    }/company/upload?orgId=${orgId}&userId=${userId}&token=${authToken}&accountType=${accountType}&parentOrgId=${parentOrgId}&primaryEmail=${encodeURIComponent(
        primaryEmail,
    )}&userName=${encodeURIComponent(userName)}&parentEmailId=${encodeURIComponent(parentEmailId)}&userRole=${userData.userRole}`;
    window.open(summonUrl, '_blank');
}

/**
 * Pass details in query params to be stored in session storage
 *
 * @param summonDetails
 */
export function openUserSummonTab(userData: {
    accountType: string;
    parentOrgId: string;
    orgId: string;
    primaryEmail: string;
    userId: string;
    userName: string;
    parentEmailId: string;
    authToken: string;
    userRole: string;
    inviteCode: string;
    platformUserId: string;
    referrer?: string;
}): void {
    const { orgId, userId, authToken, accountType, parentOrgId, primaryEmail, userName, parentEmailId, inviteCode, platformUserId } =
        userData;
    // Open a new tab with the summon details in query params
    let summonUrl = `${
        window.location.origin
    }/investor/app/scan?orgId=${orgId}&userId=${userId}&token=${authToken}&accountType=${accountType}&parentOrgId=${parentOrgId}&primaryEmail=${encodeURIComponent(
        primaryEmail,
    )}&userName=${encodeURIComponent(userName)}&parentEmailId=${encodeURIComponent(parentEmailId)}&userRole=${
        userData.userRole
    }&inviteCode=${encodeURIComponent(inviteCode)}&platformUserId=${platformUserId}&summonInternal=true`;
    if (userData.referrer) {
        summonUrl += `&referrer=${userData.referrer}`;
    }
    window.open(summonUrl, '_blank');
}

export type SummonUserData = Parameters<typeof openCompanySummonTab>[0] & { platformUserId?: string };

/**
 * onSuccess router shared by both "View Account" call-sites (header CTA + companies-list row):
 * routes a summoned session to the rocket tab when `toRocket`, else the normal company-upload tab.
 * If the rocket tab can't be opened (missing url/token) it surfaces a toast rather than doing
 * nothing. Callers compute `toRocket` from their own productName/flag/SPARK context and delegate the
 * routing here so the branch isn't duplicated per call-site. (Rocket-specific builders live in
 * `rocketSummon.ts`; this generic router only chooses between them and `openCompanySummonTab`.)
 */
export function openSummonedSession(userData: SummonUserData, toRocket: boolean): void {
    if (!toRocket) {
        openCompanySummonTab(userData);
        return;
    }
    if (!openRocketSummonTab(getRocketUrl(), userData)) {
        toast.error(ROCKET_SUMMON_OPEN_FAILED_MESSAGE);
    }
}

export function toTitleCase(str: string): string {
    if (!str) return '';
    return str
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
}
/**
 * Get domain from url
 */
export function getDomainFromUrl(url: string): string {
    if (!url) return '';
    try {
        const urlObj = new URL(url);
        return urlObj.hostname.replace('www.', '');
    } catch (error) {
        return url;
    }
}

export function getHashFromId(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = (hash << 5) - hash + char;
        hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash);
}

/**
 * Returns a human-readable string representing how long ago a date was
 * @param date - ISO 8601 date string
 * @returns "Today", "Yesterday", or "X days ago"
 * @throws Error if date is invalid or in the future
 */
export function returnDaysAgo(date: string): string {
    if (!date) {
        throw new Error('Date is required');
    }

    const parsedDate = dayjs(date);
    if (!parsedDate.isValid()) {
        throw new Error('Invalid date format');
    }

    const now = dayjs();
    if (parsedDate.isAfter(now)) {
        throw new Error('Future dates are not supported');
    }

    const diff = now.diff(parsedDate, 'day');
    return diff === 1 ? 'Yesterday' : diff === 0 ? 'Today' : `${diff} days ago`;
}

/**
 * Evaluates a dynamic formula against a set of variables with optional rule-based conditions.
 *
 * The function supports two modes:
 *
 * 1️⃣ Rule-based (opt-in via `conditionalFormulas`)
 * ```
 * {
 *   conditionalFormulas: [
 *     { condition: "status == 'SHA Signed' && cashburn != 0", expression: "unencumberedCash / cashburn" },
 *     { condition: "status == 'Not Signed'", expression: "0" }
 *   ],
 *   fallback: 0
 * }
 * ```
 * Each rule is evaluated in order. The first rule whose condition resolves to `true`
 * will have its expression evaluated and returned. If no rule matches, `fallback` is returned.
 *
 * 2️⃣ Legacy / backward-compatible mode
 * ```
 * {
 *   condition: "cashburn != 0",
 *   expression: "unencumberedCash / cashburn",
 *   fallback: 0
 * }
 * ```
 * The single condition/expression pair is evaluated. If the condition fails or the
 * expression is missing, `fallback` is returned.
 *
 * Features:
 * - Variables are substituted safely from the `variables` map.
 * - String variables are injected as quoted JS literals (e.g., `'SHA Signed'`).
 * - Existing string literals inside formulas are preserved using placeholders.
 * - Unknown variables default to `0`.
 * - Supports basic math helpers like `abs(...)` → `Math.abs(...)`.
 *
 * @param formula - Formula configuration object:
 *   - `conditionalFormulas?`: Array of `{ condition?: string; expression: string }` rules.
 *   - `condition?`: Legacy single-condition string.
 *   - `expression?`: Legacy single-expression string.
 *   - `fallback?`: Number returned when no condition matches or on error.
 *
 * @param variables - A map of variable names to values. Values may be numbers or strings.
 *   Example:
 *   `{ cashburn: 2, unencumberedCash: 10, status: 'SHA Signed' }`
 *
 * @returns The evaluated result of the first matching rule or expression.
 *          If nothing matches or an error occurs, returns `fallback` (or `0`).
 */
export function evaluateFormula(formula, variables) {
    const reservedWords = ['Math', 'Number', 'String', 'true', 'false', 'null', 'undefined', 'Infinity', 'NaN', 'abs'];

    const replaceMathFunctions = (str: string) => str.replace(/\babs\(/g, 'Math.abs(');

    const replaceVariables = (str: string) => {
        const stringLiterals: string[] = [];

        // 1️⃣ Extract and replace string literals with placeholders
        const withoutStrings = str.replace(/'([^']*)'/g, (_, val) => {
            const idx = stringLiterals.length;
            stringLiterals.push(val);
            return `__STR_${idx}__`;
        });

        // 2️⃣ Replace variable names (protect placeholders & reserved words)
        const replaced = withoutStrings.replace(/(\w+)/g, (match) => {
            // Protect placeholders like __STR_0__
            if (match.startsWith('__STR_')) return match;

            // Protect engine/runtime tokens
            if (reservedWords.includes(match)) return match;

            if (Object.prototype.hasOwnProperty.call(variables, match)) {
                const value = variables[match];

                // If the variable is a string, inject as a quoted JS literal
                if (typeof value === 'string') {
                    const escaped = value.replace(/'/g, "\\'");
                    return `'${escaped}'`;
                }

                // Numbers and booleans
                return String(value);
            }

            // Unknown variable → 0
            return '0';
        });

        // 3️⃣ Restore original string literals
        return replaced.replace(/__STR_(\d+)__/g, (_, idx) => {
            return `'${stringLiterals[Number(idx)]}'`;
        });
    };

    const evalSingle = (condition?: string, expression?: string) => {
        if (!expression) return undefined;

        const conditionWithMath = condition ? replaceMathFunctions(condition) : 'true';

        const finalCondition = conditionWithMath !== 'true' ? replaceVariables(conditionWithMath) : 'true';

        const conditionFn = new Function(`return ${finalCondition};`);

        if (!conditionFn()) return undefined;

        const expressionWithMath = replaceMathFunctions(expression);
        const finalExpression = replaceVariables(expressionWithMath);
        const expressionFn = new Function(`return ${finalExpression};`);

        return expressionFn();
    };

    try {
        if (Array.isArray(formula?.conditionalFormulas)) {
            for (const rule of formula.conditionalFormulas) {
                const result = evalSingle(rule.condition, rule.expression);
                if (result !== undefined) return result;
            }
            return formula?.fallback ?? 0;
        }

        const result = evalSingle(formula?.condition, formula?.expression);
        return result !== undefined ? result : formula?.fallback ?? 0;
    } catch (error) {
        console.error('Invalid formula evaluation', error);
    }
}

export const checkIfAnyFilterSelected = (filters: DealFilters) => {
    const filtersToCheck = ['statuses', 'dealOwners', 'dealReceivers', 'uwAnalysts', 'dcmOwners', 'leadOwners', 'products'] as const;
    type FilterKey = (typeof filtersToCheck)[number];

    return filtersToCheck.some((filter: FilterKey) => Array.isArray(filters[filter]) && (filters[filter] as string[])?.length > 0);
};

export const navigateUserAfterUnauth = (location, navigate) => {
    if (location.pathname.includes('investor/')) navigate('/investor/login');
    else if (location.pathname.includes('lender/')) navigate('/investor/login');
    else navigate('/login');
};

export const truncateTextForComments = (text: string, smaller = true) => {
    const lines = text.split('\n');
    if (lines.length === 1) {
        // If there are no newline characters, show up to 60 characters with "..."
        return lines[0].length > 20 ? lines[0].slice(0, smaller ? 20 : 40) + '...' : lines[0];
    }
    // If there are newline characters, truncate first line after 50 characters
    const truncatedLines = lines.slice(0, 1).map((line) => (line.length > 20 ? line.slice(0, smaller ? 20 : 40) + '...' : line));

    return truncatedLines.join('\n') + (lines.length > 2 ? '...' : '');
};

export const getUserTypeList = (userType: string | null, parentCategory?: string | null) => {
    const effectiveCategory = userType || parentCategory;

    switch (effectiveCategory) {
        case MANAGE_USERS_TABS.PARTNER:
            return [USER_TYPES.PARTNER_TYPE];
        case MANAGE_USERS_TABS.INVESTOR:
            return [USER_TYPES.LENDER, USER_TYPES.LENDER_TEAM];
        case MANAGE_USERS_TABS.TEAM:
            return [USER_TYPES.INVESTOR_TYPE, USER_TYPES.ADMIN_TEAM];
        case MANAGE_USERS_TABS.COMPANY:
            return [USER_TYPES.COMPANY_TYPE];
        default:
            return [];
    }
};

export const getOwnerDetails = (userType: string) => {
    switch (userType) {
        case USER_TYPE.PARTNER:
            return TALK_TO_US_OWNERS.PARTNER;
        case USER_TYPE.LENDER:
        case USER_TYPE.LENDER_TEAM:
            return TALK_TO_US_OWNERS.LENDER;
        case USER_TYPE.COMPANY:
            return TALK_TO_US_OWNERS.BORROWER;
        default:
            return TALK_TO_US_OWNERS.BORROWER;
    }
};

export const formatDate = (dateString: string | undefined | null): string => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = date.toLocaleString('default', { month: 'short' });
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
};

export const truncateByCharacters = (input: string, limit: number) => {
    if (!input) return '';
    return input.length <= limit ? input : input.slice(0, limit) + '...';
};

export const deepCompare = (obj1: any, obj2: any): boolean => {
    if (typeof obj1 !== 'object' || obj1 === null || typeof obj2 !== 'object' || obj2 === null) {
        return obj1 === obj2;
    }

    if (obj1 instanceof Map && obj2 instanceof Map) {
        if (obj1.size !== obj2.size) {
            return false;
        }
        for (const [key, value] of obj1) {
            if (!obj2.has(key) || !deepCompare(value, obj2.get(key))) {
                return false;
            }
        }
        return true;
    }

    if (Array.isArray(obj1) && Array.isArray(obj2)) {
        if (obj1.length !== obj2.length) {
            return false;
        }
        return obj1.every((item, index) => deepCompare(item, obj2[index]));
    }

    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    if (keys1.length !== keys2.length) {
        return false;
    }

    for (const key of keys1) {
        if (!keys2.includes(key) || !deepCompare(obj1[key], obj2[key])) {
            return false;
        }
    }

    return true;
};

export const isLenderType = (userType: string = '') => {
    if (isEmpty(userType)) {
        userType = sessionStorage.getItem('user_data')
            ? JSON.parse(sessionStorage.getItem('user_data') || '{}').accountType
            : JSON.parse(localStorage.getItem('user_data') || '{}').accountType;
    }
    return [USER_TYPES.LENDER, USER_TYPES.LENDER_TEAM].includes(userType);
};

/**
 * Replaces Handlebars placeholders in a string with actual values from templateValues.
 * Supports both regular {{key}} and nested {{{key}}} placeholder patterns.
 *
 * Processing Order:
 * 1. First processes nested triple braces {{{key}}} which may resolve to {{value}} or direct values
 * 2. Then processes regular double braces {{key}} to final values
 * 3. Repeats until no more replacements can be made or max iterations reached
 *
 * Features:
 * - Handles multiple placeholders of the same type in a single template
 * - Processes placeholders from left to right for predictable results
 * - Supports recursive/nested placeholder resolution (e.g., {{{key}}} -> {{value}} -> finalValue)
 * - Prevents infinite loops with iteration limits and change detection
 * - Gracefully handles missing keys by leaving placeholders unchanged
 *
 * @param template - String containing Handlebars placeholders like {{variableName}} or {{{variableName}}}
 * @param templateValues - Object containing key-value pairs for placeholder replacement
 * @returns String with all resolvable placeholders replaced with their corresponding values
 */
export const replaceTemplatePlaceholders = (template: string, templateValues: any): string => {
    if (!template || typeof template !== 'string') {
        return '';
    }

    let result = template;
    let previousResult = '';
    let iterations = 0;
    const maxIterations = 10; // Prevent infinite loops

    // Keep processing until no more changes occur or max iterations reached
    while (result !== previousResult && iterations < maxIterations) {
        previousResult = result;
        iterations++;

        // First, handle nested braces like {{{key}}} -> {{value}} or actualValue
        while (result.includes('{{{') && result.includes('}}}')) {
            let startIndex = result.indexOf('{{{');
            if (startIndex === -1) break;

            let endIndex = result.indexOf('}}}', startIndex);
            if (endIndex === -1) break;

            // Extract the key between {{{ and }}}
            const innerKey = result.substring(startIndex + 3, endIndex).trim();
            const replacement = templateValues[innerKey] || `{{{${innerKey}}}}`;

            // Replace this specific occurrence
            result = result.substring(0, startIndex) + replacement + result.substring(endIndex + 3);

            // If no replacement was made (key not found), break to avoid infinite loop
            if (replacement === `{{{${innerKey}}}`) {
                break;
            }
        }
        // Then, handle regular double braces {{key}} -> value
        Object.keys(templateValues).forEach((key) => {
            const placeholder = `{{${key}}}`;
            const value = templateValues[key] || '';

            // Replace all occurrences of this placeholder
            while (result.includes(placeholder)) {
                result = result.replace(placeholder, value);
            }
        });
    }

    // Log if we hit max iterations (potential infinite loop)
    if (iterations >= maxIterations) {
        console.warn('Template placeholder replacement hit maximum iterations. Possible circular reference.');
    }

    return result;
};

type FilterOperator = 'IN' | 'EQUALS' | 'BETWEEN';

/**
 * Transforms dynamic filters based on the provided prefixes and operator.
 *
 * @param filters - The filters object to transform.
 * @param dynamicFieldPrefixes - The prefixes of dynamic fields to check.
 * @param defaultOperator - The default operator to use if a dynamic field is found.
 */
export const transformDynamicFilters = (
    filters: Record<string, any>,
    dynamicFieldPrefixes: string[] = ['customFields.', 'owners.'],
    defaultOperator: FilterOperator = 'IN',
): Record<string, { operator: FilterOperator; value: any[]; valueType?: string }> => {
    const dynamicFields: Record<string, { operator: FilterOperator; value: any[]; valueType?: string }> = {};

    Object.entries(filters).forEach(([key, value]) => {
        const isDynamicField = dynamicFieldPrefixes.some((prefix) => key.startsWith(prefix));

        if (isDynamicField && Array.isArray(value) && value.length > 0) {
            // Check if this is a DateRangeFilter by looking for gte/lte structure (fallback)
            dynamicFields[key] = {
                operator: defaultOperator,
                value,
            };
        } else if (isDynamicField && typeof value === 'object' && !Array.isArray(value)) {
            // If the value is an object, i.e we send the operator and any other keys from the filter renderer
            dynamicFields[key] = value;
        }
    });

    return dynamicFields;
};

export const isAppliedFiltersEmpty = (filters: Record<string, any[]> | undefined | null): boolean => {
    if (!filters || Object.keys(filters).length === 0) {
        return true;
    }

    // Check if all values are empty arrays
    return Object.values(filters).every((value) => Array.isArray(value) && value.length === 0);
};

/**
 *Persist the state of customFields while sending in the api payload.
 *This is done to persist the undefined or null values of customFields in order to clear the fields in BE and UI.
 *avoid from sending customFields as {} in the api payload.
 * @param customFields - The custom fields object to clean
 * @returns The cleaned custom fields object
 */
export function cleanCustomFields(customFields: any) {
    if (!customFields || typeof customFields !== 'object') return customFields;

    const cleaned: Record<string, any> = {};
    Object.entries(customFields).forEach(([key, value]) => {
        cleaned[key] = value === undefined ? null : value;
    });
    return cleaned;
}

/**
 * Creates single select dropdown options from data
 * @param data - Array of organization objects with organizationId and organizationDisplayName
 * @returns Object with organizationId as keys and dropdown option objects as values
 */
export const createDropdownOptions = (
    data: Array<{ organizationId: string; organizationDisplayName: string }> | null | undefined,
): Record<string, any> => {
    if (!data || !Array.isArray(data)) return {};
    const dropdownOptions = {};
    data.forEach((org) => {
        dropdownOptions[org.organizationId] = {
            label: org.organizationDisplayName,
            value: org.organizationId,
            backgroundColor: '#EBEBEB',
            labelColor: '#000000',
            customCaret: 'https://fl-fe-assets.s3.ap-south-1.amazonaws.com/svg/grey-dropdown-caret.svg',
        };
    });
    return dropdownOptions;
};

export const copyInviteToClipboard = (inviteCode: string) => {
    const url = new URL(`https://${window.location.hostname}`);
    url.searchParams.set('inviteCode', inviteCode);
    copyTextToClipboard(url.href)
        .then(() => {
            toast.success('Invite Link Copied');
        })
        .catch(() => toast.success('Something went wrong'));
};
export const isSafeUrl = (url: string): boolean => {
    try {
        const u = new URL(url, window.location.origin);
        const allowed = ['https:', 'mailto:'];
        return allowed.includes(u.protocol);
    } catch {
        return false;
    }
};

export const isPnoUser = (userType: string = '') => {
    return userType === USER_TYPES.ADMIN_TEAM || userType === USER_TYPES.INVESTOR_TYPE;
};

/**
 * Extracts phone number and country code index from mobileNumber string
 * @param mobileNumber - The full mobile number string (may include country code)
 * @param userCountryCode - Optional country code from user data
 * @param countryCodes - Array of available country codes
 * @returns Object containing extracted phoneNumber and countryCodeIndex
 */
export const extractPhoneNumber = (
    mobileNumber: string,
    userCountryCode: string | undefined,
    countryCodes: CountryCodeData[],
): ExtractPhoneNumberResult => {
    if (!mobileNumber?.trim()) {
        return { phoneNumber: '', countryCodeIndex: -1 };
    }

    if (!countryCodes?.length) {
        return { phoneNumber: mobileNumber.replace(/[+\s-]/g, ''), countryCodeIndex: -1 };
    }

    let phoneNumber = '';
    let countryCodeIndex = -1;

    // First, try to match country code from userData.countryCode
    if (userCountryCode) {
        const matchedCountryCode = countryCodes.find((cc) => cc.countryCode === userCountryCode);
        if (matchedCountryCode) {
            countryCodeIndex = countryCodes.findIndex((cc) => cc.countryCode === userCountryCode);
            const callingCodePrefix = `+${matchedCountryCode.callingCode}`;
            const callingCodeWithSpace = `${callingCodePrefix} `;

            // Extract phone number by removing the country code prefix
            if (mobileNumber.startsWith(callingCodeWithSpace)) {
                phoneNumber = mobileNumber.substring(callingCodeWithSpace.length);
                return { phoneNumber, countryCodeIndex };
            }
            if (mobileNumber.startsWith(callingCodePrefix)) {
                phoneNumber = mobileNumber.substring(callingCodePrefix.length);
                return { phoneNumber, countryCodeIndex };
            }
            // If no prefix found, clean the number
            phoneNumber = mobileNumber.replace(/[+\s-]/g, '');
            return { phoneNumber, countryCodeIndex };
        }
    }

    // Fallback: parse from mobileNumber string
    if (!mobileNumber.startsWith('+')) {
        phoneNumber = mobileNumber.replace(/[+\s-]/g, '');
        return { phoneNumber, countryCodeIndex: -1 };
    }

    // Try to match country code from the mobile number prefix
    for (const cc of countryCodes) {
        const callingCode = `+${cc.callingCode}`;
        const callingCodeWithSpace = `${callingCode} `;

        if (mobileNumber.startsWith(callingCodeWithSpace)) {
            phoneNumber = mobileNumber.substring(callingCodeWithSpace.length);
            countryCodeIndex = countryCodes.findIndex((c) => c.countryCode === cc.countryCode);
            return { phoneNumber, countryCodeIndex };
        }
        if (mobileNumber.startsWith(callingCode)) {
            phoneNumber = mobileNumber.substring(callingCode.length);
            countryCodeIndex = countryCodes.findIndex((c) => c.countryCode === cc.countryCode);
            return { phoneNumber, countryCodeIndex };
        }
    }

    // Default fallback: clean the number
    phoneNumber = mobileNumber.replace(/[+\s-]/g, '');
    return { phoneNumber, countryCodeIndex: -1 };
};

export const filterColumnToMap = (map, column) => {
    const existing = map.get(column.field);

    if (!existing || (column?.show !== false && existing?.show === false)) {
        map.set(column.field, column);
    }
};

/**
 * Formats a ratio value with optional unit and rounding
 * @param value - The value to format (can be any type)
 * @param unit - Optional unit string to append (default: '')
 * @param roundOffRequired - Whether to round to 1 decimal place (true) or 2 decimal places (false, default)
 * @param decimalsOverride - Optional explicit decimal count; when provided, takes precedence over roundOffRequired (use 0 for integer day-count ratios)
 * @returns Formatted string with unit, or '-' for invalid/null/undefined values
 */
export function formatRatioValue(value: any, unit: string = '', roundOffRequired: boolean = false, decimalsOverride?: number): string {
    // Explicitly check for null, undefined, or empty string (but allow 0)
    if (value === null || value === undefined || value === '') {
        return '-';
    }
    const numericValue = parseFloat(String(value));
    if (!Number.isFinite(numericValue)) {
        return '-';
    }
    // Format value based on decimalsOverride first, then roundOffRequired
    const decimals = decimalsOverride !== undefined ? decimalsOverride : roundOffRequired ? 1 : 2;
    let formatted: string;
    if (numericValue < 0) {
        // Format negative values with parentheses
        formatted = `(${Math.abs(numericValue).toFixed(decimals)})`;
    } else {
        formatted = numericValue.toFixed(decimals);
    }
    return `${formatted}${unit}`;
}

/**
 * Safely converts a value to a number, returning 0 if conversion fails or value is invalid
 * @param value - The value to convert to a number (can be any type)
 * @returns The numeric value, or 0 if conversion fails, value is null, undefined, or NaN
 */
export function safeToNumber(value: any): number {
    if (value === null || value === undefined || value === '') {
        return 0;
    }

    // If already a valid number, return it
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }

    try {
        const num = Number(value);
        return Number.isFinite(num) ? num : 0;
    } catch (error) {
        return 0;
    }
}

export const isValidNumber = (value: string) => {
    return value?.includes('-') || !isNaN(Number(value));
};

/**
 * Converts a value to a number when it is present and coercible; otherwise returns undefined.
 * Use for optional numeric fields (e.g. form/API payloads) where empty/null should not become 0.
 */
export function toNumber(value?: string | number | null): number | undefined {
    if (value == null || value === '') return undefined;
    const num = Number(value);
    return Number.isNaN(num) ? undefined : num;
}

/**
 * Type guard: true when value is a number, not NaN, and not zero.
 * Use when building payloads to omit "missing" numeric fields (e.g. avoid sending 0 for absent amount/roi/tenure).
 */
export function hasNonZeroValue(value: number | undefined | null): value is number {
    return typeof value === 'number' && !Number.isNaN(value) && value !== 0;
}

/**
 * Converts an ALL_CAPS or UPPER CASE string to Title Case.
 * Handles hyphenated words (e.g. "CANCELLED SUO-MOTO" → "Cancelled Suo-Moto").
 */
export function toTitleCaseWithSeparators(str: string): string {
    return str.toLowerCase().replace(/(?:^|[\s-])(\w)/g, (match) => match.toUpperCase());
}

/**
 * Strips a name to a lowercase alphanumeric key for cross-component matching.
 * Used to match director / entity rows across tabs (Related Parties, Risk Indicators,
 * Legal Cases) where naming may differ in whitespace / punctuation / casing.
 *
 *   "PROBE Director TWO" -> "probedirectortwo"
 *   "M/s. ACME-Co Ltd."  -> "msacmecoltd"
 *
 * Always pair both sides (URL param value AND DOM data attribute) through this helper
 * so lookups stay symmetric.
 */
export function normalizeNameKey(name?: string | null): string {
    return (name ?? '')
        .toString()
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '');
}

export const navigateToDealsPage = (orgId: string, navigate: (path: string) => void) => {
    if (!orgId) {
        toast.error('No organization found');
        return;
    }
    navigate(`/investor/app/company-deals/${orgId}?fetchType=all&page=1&type=scan&tab=deals`);
};

export const openUserPortfolioInNewTab = (orgId: string, permissionType: string) => {
    if (!orgId) return;
    const url = `/investor/app/portfolio/${orgId}?fetchType=all&page=1&type=${permissionType?.toLowerCase()}&tab=aicaReport&selectedTab=overview`;
    window.open(url, '_blank', 'noopener,noreferrer');
};

export const openCompanyDealsInNewTab = (orgId: string) => {
    if (!orgId) return;
    const url = `/investor/app/company-deals/${orgId}?fetchType=all&page=1&type=scan&tab=deals`;
    window.open(url, '_blank', 'noopener,noreferrer');
};

/** Total combined byte size of a list of files (treats a missing size as 0). */
export const sumAttachmentBytes = (files: { fileSizeBytes?: number }[]): number =>
    (files ?? []).reduce((sum, file) => sum + (file?.fileSizeBytes ?? 0), 0);

/**
 * Humanize a cost-weighted token count for the AskAica message footer (RECUR-26780).
 * Round-half-up to one decimal + "k"; "<1k" below 1000; whole "k" at/above 1,000,000.
 */
export function humanizeTokens(n: number): string {
    if (n == null || Number.isNaN(n)) return '';
    if (n < 1000) return '<1k';
    if (n >= 1_000_000) return `${Math.round(n / 1000)}k`;
    const tenths = Math.floor(n / 100 + 0.5) / 10; // round-half-up to 1 decimal
    return `${tenths.toFixed(1)}k`; // keeps trailing .0 -> "1.0k"
}

/**
 * Opens a server-hosted document in a new tab via a fresh presigned URL (read-only).
 * Best-effort: logs and no-ops on a failed/empty presign rather than throwing.
 *
 * The blank tab is opened SYNCHRONOUSLY inside the click gesture, then navigated
 * once the presigned URL resolves. Opening the tab AFTER the `await` puts it
 * outside the user-gesture window, which mobile Safari/Chrome silently block as
 * a popup — the reason attachment taps did nothing on phones.
 */
export async function openDocument(docId: string) {
    const newTab = window.open('', '_blank');
    // Drop the opener reference for the same security posture as the old
    // `noopener` call (noopener can't be used here — it makes window.open
    // return null, and we need the handle to navigate the tab).
    if (newTab) {
        try {
            newTab.opener = null;
        } catch {
            /* cross-origin guard — safe to ignore */
        }
    }
    try {
        const res = await getPresignedUrl({ docId: Number(docId) }, false);
        if (res?.uri) {
            if (newTab) newTab.location.href = res.uri;
            // Fallback if the synchronous open was still blocked (rare).
            else window.open(res.uri, '_blank', 'noopener,noreferrer');
        } else {
            if (newTab) newTab.close();
            console.error('Attachment open failed — no presigned URI returned', { docId });
        }
    } catch (err) {
        if (newTab) newTab.close();
        console.error('Attachment open failed', { docId, err });
    }
}

/**
 * RECUR-21135: True when a brand/display name exists and is meaningfully different from the
 * legal (registered) name — i.e. there's a distinct brand worth surfacing separately.
 * Falsy-safe: an empty-string (or missing) brand is never "distinct".
 */
export function hasDistinctBrand(brand?: string | null, legal?: string | null): boolean {
    return !!brand && brand !== legal;
}

/**
 * RECUR-21135: Resolves the primary/secondary company-name slots shared by search results,
 * cell renderers, and the Overview detail header — the brand is the primary name with an
 * empty-safe fallback to the legal (registered) name; the legal name becomes the secondary
 * label only when the brand is distinct from it, otherwise `fallbackSecondary` is used
 * (e.g. website/CIN).
 *
 * Falsy-safe throughout (`||`, not `??`) so an empty-string brand or legal name correctly
 * falls through to the next candidate instead of rendering blank.
 */
export function resolvePrimarySecondaryNames(
    brand?: string | null,
    legal?: string | null,
    fallbackSecondary?: string | null,
): { primary: string; secondary: string; isDistinct: boolean } {
    const isDistinct = hasDistinctBrand(brand, legal);
    return {
        primary: brand || legal || '',
        secondary: isDistinct ? legal || '' : fallbackSecondary || '',
        isDistinct,
    };
}

/**
 * Vendors return partially masked values in-band ("*******001", "XXXXX1234F") — there is no
 * separate flag, so three or more consecutive mask characters is the marker.
 */
export function isMaskedValue(value?: string | null): boolean {
    return !!value && /[*X]{3,}/.test(value.trim());
}
